<template>
    <div class="relative" :class="wrapperClass">
        <label
            v-if="label"
            :for="id"
            class="leading-none self-center text-black mr-1"
        >
            {{ label }}
        </label>
        <select
            v-model="internalValue"
            @change="onChange"
            :id="id"
            class="form-control form-control-sm form-control-fixed bg-white"
            :class="[
                { loading: loading },
                selectClass
            ]"
            :disabled="disabled || loading"
            :aria-label="`Select ` + (label || '...')"
        >
            <template v-for="(option, index) in options">
                <optgroup v-if="option.groups && hasGroups" :label="option.label" :key="index">
                    <option v-for="(o, oIndex) in option.groups" :value="o.value" :key="oIndex">
                        {{ o.label }}
                    </option>
                </optgroup>
                <option v-else :class="{'bg-yellow-200' : option.highlight}" :value="option.value" :key="option.value">
                    {{ option.label }}
                </option>
            </template>
        </select>
        <div v-if="loading" class="loading-spinner"></div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';

const props = defineProps({
    wrapperClass: String,
    selectClass: String,
    label: String,
    options: {
        type: Array,
        default: () => [],
    },
    hasGroups: {
        type: Boolean,
        default: false,
    },
    disabled: Boolean,
    loading: {
        type: Boolean,
        default: false,
    },
    modelValue: [String, Number, Object],
});

const emit = defineEmits(['update:modelValue', 'change', 'selected']);

const id = ref(`select-${Math.random().toString(16).slice(2)}`);

const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// We track the selected option's value internally
const selectedValue = ref(props.modelValue);

// We'll use a computed property with both a getter and a setter
const internalValue = computed({
    get() {
        return selectedValue.value;
    },

    set(value) {
        // Find the corresponding option object and emit its value
        const selectedOption = props.options.find(option => option.value === value);
        selectedValue.value = selectedOption ? selectedOption.value : null;
        emit('update:modelValue', selectedValue.value);
    },
});

// Watch for changes in modelValue prop to update the local selected value
watch(() => props.modelValue, newValue => {
    selectedValue.value = newValue;
});

const onChange = (event) => {
    // When the select changes, the internalValue setter will handle updating the value
    emit('selected', selectedValue.value);
    emit('change', selectedValue.value);
};

onMounted(() => {
    id.value = `select-${generateUUID()}`;

    if (props.options && (selectedValue.value === null || selectedValue.value === undefined)) {
        internalValue.value = props.options[0]?.value;
    }
});
</script>
